import React, { useState, useEffect, useCallback } from "react";
import Particles from "react-tsparticles";
import { loadFull } from "tsparticles";
import { ParticlesParams } from "../Schemas/Particles";

export const Options = () => {
  var { lsTheme, lsSnow } = "";
  try {
    lsTheme = localStorage.getItem("theme");
    lsSnow = JSON.parse(localStorage.getItem("snow"));
  } catch (e) {
    console.error(`All Cookies blocked - Error: ${e.message}`);
  }

  const getPreferredTheme = () =>
    window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";

  const [themeMode, setThemeMode] = useState(
    lsTheme === "dark" || lsTheme === "light" || lsTheme === "auto"
      ? lsTheme
      : "auto"
  );

  const [effectiveTheme, setEffectiveTheme] = useState(
    themeMode === "auto" ? getPreferredTheme() : themeMode
  );

  const [icon, setIcon] = useState("bx-desktop");
  const [snow, setSnow] = useState(typeof lsSnow === "boolean" ? lsSnow : false);

  useEffect(() => {
    if (themeMode !== "auto") {
      setEffectiveTheme(themeMode);
      return;
    }

    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const update = () => setEffectiveTheme(media.matches ? "dark" : "light");

    update();

    if (media.addEventListener) {
      media.addEventListener("change", update);
      return () => media.removeEventListener("change", update);
    }

    media.addListener(update);
    return () => media.removeListener(update);
  }, [themeMode]);

  const setThemeColorMeta = useCallback((color) => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) return;
    meta.setAttribute("content", color);
  }, []);

  const setThemeColorFromCssVar = useCallback(
    (cssVarName) => {
      const color = getComputedStyle(document.body)
        .getPropertyValue(cssVarName)
        .trim();

      if (color) setThemeColorMeta(color);
    },
    [setThemeColorMeta]
  );

  useEffect(() => {
    document.body.classList[effectiveTheme === "dark" ? "add" : "remove"](
      "dark-theme"
    );

    // Keep browser UI (mobile status bar/address bar) aligned with the active theme.
    // Uses CSS design tokens so it stays in sync with light/dark changes.
    setThemeColorFromCssVar("--color-surface");

    localStorage.setItem("theme", themeMode);
    localStorage.setItem("snow", snow);
  }, [themeMode, effectiveTheme, snow, setThemeColorFromCssVar]);

  useEffect(() => {
    const nextIcon =
      themeMode === "auto"
        ? "bx-desktop"
        : effectiveTheme === "dark"
          ? "bx-moon"
          : "bx-sun";
    setIcon(nextIcon);
  }, [themeMode, effectiveTheme]);

  const particlesInit = useCallback(async (engine) => {
    await loadFull(engine);
  }, []);

  const SnowEffect = () =>
    snow && effectiveTheme === "dark" && (
      <Particles
        id="tsparticles"
        className="body-particles"
        init={particlesInit}
        options={ParticlesParams}
      />
    );

  const _enableSnow = () => setSnow(!snow);
  const _toggleTheme = () => {
    setThemeMode((current) => {
      if (current === "light") return "dark";
      if (current === "dark") return "auto";
      return "light";
    });
  };

  return (
    <div className="home-options">
      {effectiveTheme === "dark" && (
        <i
          className="bx bx-cloud-snow enable-snow"
          title="Activate Snow"
          id="snow-button"
          onClick={_enableSnow}
        />
      )}
      <SnowEffect />
      <i
        className={`bx ${icon} change-theme`}
        title={`Theme: ${themeMode === "auto" ? `Auto (${effectiveTheme})` : effectiveTheme}`}
        id="theme-button"
        onClick={_toggleTheme}
      />
    </div>
  );
};